import React, { useEffect, useMemo, useState } from 'react';
import { array, bool, func, object } from 'prop-types';
import classNames from 'classnames';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { propTypes } from '../../util/types';
import { camelize } from '../../util/string';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Page, LayoutSingleColumn, NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import { loadExperts, ASSET_NAME } from './ExpertsPage.duck';
import css from './ExpertsPage.module.css';

// ================ Roster configuration ================ //

// The roster's discipline sections (titles + which category keys feed each)
// are configured in the Console under Content > Pages > our-experts: the
// `expert-categories` section's blocks. `deriveDisciplines` below reads them
// from the hosted asset. This built-in list is only a fallback for when the
// asset is missing or empty, so the page still renders sensible groups.
const DEFAULT_DISCIPLINES = [
  {
    id: 'written-story',
    keys: ['written-story'],
    title: 'Written Story Masters',
    blurb: 'Books, columns, and long-form craft from people who publish for a living.',
  },
  {
    id: 'business-story',
    keys: ['business-story'],
    title: 'Business Story Masters',
    blurb: 'Pitches, positioning, and the narratives that raise money and close deals.',
  },
  {
    id: 'visual-story',
    keys: ['visual-story'],
    title: 'Creative Visual Story Masters',
    blurb: 'Illustration, branding, and design — how a story looks before it speaks.',
  },
  {
    id: 'education-coaching',
    keys: ['education-coaching'],
    title: 'Coaching & Technology Masters',
    blurb: 'Delivery, speaking, and presence — how a story lands in the room.',
  },
  {
    id: 'content-master',
    keys: ['content-master', 'publishing'],
    title: 'Content Masters',
    blurb: 'High-signal narratives built for digital and social platforms.',
  },
  {
    id: 'motion-story',
    keys: ['motion-story'],
    title: 'Motion Story Masters',
    blurb: 'Screenplays, film, and animation — stories that move.',
  },
];

// Read the section-defining blocks from the `expert-categories` section of the
// hosted `our-experts` asset. Each block carries a title, an optional
// description, and a comma-separated `categoryKey` (Console's block editor may
// store the key as `categoryKey` or fall back to the block's name). Returns
// null when the asset isn't present so the caller can use DEFAULT_DISCIPLINES.
const deriveDisciplines = pageAssetsData => {
  const assetData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;
  const sections = assetData?.sections || [];
  const categoriesSection = sections.find(
    s => s.sectionId === 'expert-categories' || s.sectionType === 'expert-categories'
  );
  const blocks = categoriesSection?.blocks || [];

  const disciplines = blocks
    .map((block, index) => {
      const rawKey = block.categoryKey || block.blockName || '';
      const keys = rawKey
        .split(',')
        .map(k => k.trim())
        .filter(Boolean);
      const title = block.title?.content?.trim();
      if (keys.length === 0 || !title) {
        return null;
      }
      return {
        id: block.blockId || block.blockName || `category-${index}`,
        keys,
        title,
        blurb: block.text?.content?.trim() || '',
      };
    })
    .filter(Boolean);

  return disciplines.length > 0 ? disciplines : null;
};

// Role eyebrow shown on each card, keyed by category.
const ROLE_LABELS = {
  'written-story': 'Written Story Master',
  'motion-story': 'Motion Story Master',
  'visual-story': 'Visual Story Master',
  'business-story': 'Business Story Master',
  'content-master': 'Content Master',
  publishing: 'Publishing Master',
  'education-coaching': 'Coaching Master',
};

// How many cards a discipline shows before the "+N more" tile.
const VISIBLE_BEFORE_EXPAND = 8;

const CURRENCY_SYMBOLS = { USD: '$', EUR: '€', GBP: '£', CAD: 'CA$', AUD: 'A$' };

// Avatar fallback gradients — same family as the marketing site (base.css g1–g6).
const AVATAR_GRADIENTS = [
  'linear-gradient(135deg,#EF2D56,#F5A623)',
  'linear-gradient(135deg,#6C4CF1,#EF2D56)',
  'linear-gradient(135deg,#F5A623,#12A87B)',
  'linear-gradient(135deg,#12A87B,#6C4CF1)',
  'linear-gradient(135deg,#EF2D56,#6C4CF1)',
  'linear-gradient(135deg,#F5A623,#EF2D56)',
];

// ================ Data helpers ================ //

// The roster endpoint returns a lean, flat expert shape
// (see server/api/users/all-experts.js), so fields are read directly.
const getCategory = expert => expert?.category || null;

// Only experts with an approved category are listed. Experts without the
// categoryApproved flag are shown for backward compatibility.
const hasApprovedCategory = expert => {
  const category = getCategory(expert);
  const approved = expert?.categoryApproved;
  return !!category && (approved === true || approved === undefined);
};

const isTopAdvisor = expert => !!expert?.isTopAdvisor;

// Top advisors first, then alphabetical.
const compareExperts = (a, b) => {
  const aTop = isTopAdvisor(a);
  const bTop = isTopAdvisor(b);
  if (aTop && !bTop) return -1;
  if (!aTop && bTop) return 1;
  return (a?.displayName || '').localeCompare(b?.displayName || '');
};

const getInitials = name =>
  (name || '')
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();

const gradientFor = name => {
  const hash = (name || '').split('').reduce((a, b) => {
    a = (a << 5) - a + b.charCodeAt(0);
    return a & a;
  }, 0);
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
};

const formatPrice = (price, currency) => `${CURRENCY_SYMBOLS[currency] || currency}${price}`;

// Group approved experts into the discipline sections. Categories that don't
// match any configured discipline are collected into a trailing group so no
// Master silently disappears from the roster.
const groupExperts = (experts, disciplines) => {
  const approved = (experts || []).filter(hasApprovedCategory);
  const knownKeys = new Set(disciplines.flatMap(d => d.keys));

  const groups = disciplines.map(discipline => ({
    ...discipline,
    experts: approved.filter(e => discipline.keys.includes(getCategory(e))).sort(compareExperts),
  }));

  const leftovers = approved.filter(e => !knownKeys.has(getCategory(e))).sort(compareExperts);
  if (leftovers.length > 0) {
    groups.push({
      id: 'more-masters',
      keys: [],
      title: 'More Masters',
      blurb: 'Specialists beyond the core disciplines.',
      experts: leftovers,
    });
  }

  return groups.filter(g => g.experts.length > 0);
};

// ================ Presentational pieces ================ //

// The editor's red pen — drawn under "your Master" on load.
const PenUnderline = () => (
  <svg className={css.pen} viewBox="0 0 200 12" preserveAspectRatio="none" aria-hidden="true">
    <path d="M3,9 C48,3 128,10 197,5" />
  </svg>
);

const CaveatPointer = () => (
  <span className={css.searchAnno} aria-hidden="true">
    know the name?
    {/* hooks left, back into the search field, with the arrowhead at its edge */}
    <svg viewBox="0 0 48 42">
      <path d="M43,8 C34,26 22,34 6,34" fill="none" />
      <polyline points="14,26 5,34 13,40" fill="none" />
    </svg>
  </span>
);

const ExpertCard = props => {
  const { expert } = props;
  const displayName = expert.displayName || 'Master';
  const credential = expert.credential;
  const price = expert.startingAtPrice;
  const currency = expert.startingAtPriceCurrency || 'USD';
  const roleLabel = ROLE_LABELS[expert.category] || 'Master';
  const profileImage = expert.profileImage;

  return (
    <NamedLink
      className={css.card}
      name="ProfilePage"
      params={{ id: expert.slug || expert.id }}
    >
      {isTopAdvisor(expert) ? <span className={css.topBadge}>Top Advisor</span> : null}
      <div
        className={css.cardPhoto}
        style={profileImage?.url ? null : { background: gradientFor(displayName) }}
      >
        {profileImage?.url ? (
          <img
            className={css.cardImage}
            alt={displayName}
            src={profileImage.url}
            srcSet={
              profileImage.url2x ? `${profileImage.url} 1x, ${profileImage.url2x} 2x` : undefined
            }
            loading="lazy"
          />
        ) : (
          <span className={css.cardInitials}>{getInitials(displayName)}</span>
        )}
      </div>
      <div className={css.cardBody}>
        <p className={css.cardRole}>{roleLabel}</p>
        <h3 className={css.cardName}>{displayName}</h3>
        {credential ? <p className={css.cardBio}>{credential}</p> : null}
        {price ? (
          <p className={css.cardPrice}>
            Sessions from <b>{formatPrice(price, currency)}</b>
          </p>
        ) : null}
      </div>
    </NamedLink>
  );
};

const DisciplineSection = props => {
  const { id, title, blurb, experts, alwaysExpanded = false } = props;
  const [expanded, setExpanded] = useState(false);
  const showAll = expanded || alwaysExpanded;
  const visible = showAll ? experts : experts.slice(0, VISIBLE_BEFORE_EXPAND);
  const hiddenCount = experts.length - visible.length;

  // The "+N more … Masters" tile appends its own "Master(s)", so strip a
  // trailing "Master"/"Masters" the section title (from the asset) already
  // carries — e.g. "Written Story Masters" → "Written Story".
  const shortLabel = title.replace(/\s+Masters?$/i, '');

  return (
    <section id={id} className={css.disc} aria-label={title}>
      <header className={css.discHead}>
        <div>
          <h2 className={css.discTitle}>{title}</h2>
          <p className={css.discSub}>{blurb}</p>
        </div>
        <span className={css.discCount}>
          <b>{experts.length}</b> {experts.length === 1 ? 'Master' : 'Masters'}
        </span>
      </header>
      <div className={css.grid}>
        {visible.map(expert => (
          <ExpertCard key={expert.id} expert={expert} />
        ))}
        {hiddenCount > 0 ? (
          <button type="button" className={css.moreTile} onClick={() => setExpanded(true)}>
            <span className={css.moreDots} aria-hidden="true">
              <i />
              <i />
              <i />
              <i />
            </span>
            <span className={css.moreCopy}>
              <b>+{hiddenCount} more</b> {shortLabel} {hiddenCount === 1 ? 'Master' : 'Masters'} on
              the roster
            </span>
            <span className={css.moreGo}>Show them all →</span>
          </button>
        ) : null}
      </div>
    </section>
  );
};

const SkeletonRoster = () => (
  <div className={css.disc} aria-hidden="true">
    <div className={css.skeletonHead}>
      <span className={css.skeletonLine} style={{ width: '180px' }} />
      <span className={css.skeletonLine} style={{ width: '320px' }} />
    </div>
    <div className={css.grid}>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className={css.skeletonCard}>
          <span className={css.skeletonPhoto} />
          <span className={css.skeletonBody}>
            <span className={css.skeletonLine} style={{ width: '40%' }} />
            <span className={css.skeletonLine} style={{ width: '60%' }} />
            <span className={css.skeletonLine} style={{ width: '85%' }} />
          </span>
        </div>
      ))}
    </div>
  </div>
);

// ================ Page ================ //

export const ExpertsPageComponent = props => {
  const {
    experts,
    expertsInProgress,
    expertsError,
    pageAssetsData,
    scrollingDisabled,
    onLoadExperts,
  } = props;

  const [searchQuery, setSearchQuery] = useState('');

  // If SSR didn't populate the roster (e.g. the data load failed server-side),
  // fetch once on mount. `loadAttempted` also gates the failed state below, so a
  // stale error from a failed SSR load shows the skeleton (not "roster didn't
  // load") until the client retry has actually completed.
  const [loadAttempted, setLoadAttempted] = useState(false);
  useEffect(() => {
    if (!loadAttempted && experts.length === 0 && !expertsInProgress) {
      onLoadExperts().finally(() => setLoadAttempted(true));
    }
  }, []);

  // Discipline sections come from the Console-hosted `our-experts` asset; fall
  // back to the built-in list only when the asset defines no usable sections.
  const disciplines = useMemo(() => deriveDisciplines(pageAssetsData) || DEFAULT_DISCIPLINES, [
    pageAssetsData,
  ]);

  const groups = useMemo(() => groupExperts(experts, disciplines), [experts, disciplines]);
  const rosterCount = useMemo(() => groups.reduce((sum, g) => sum + g.experts.length, 0), [groups]);

  // Name/headline search across the whole roster.
  const query = searchQuery.trim().toLowerCase();
  const isSearching = query.length > 0;
  const searchResults = useMemo(() => {
    if (!query) return [];
    return (experts || [])
      .filter(hasApprovedCategory)
      .filter(expert => {
        const name = (expert.displayName || '').toLowerCase();
        const credential = (expert.credential || '').toLowerCase();
        return name.includes(query) || credential.includes(query);
      })
      .sort(compareExperts);
  }, [experts, query]);

  // Show the skeleton while a load is in progress OR before the first client
  // load has finished; only show the failed state once an attempt has actually
  // completed with an error (prevents the SSR-error flash on first paint).
  const isLoading = rosterCount === 0 && (expertsInProgress || !loadAttempted);
  const isFailed = rosterCount === 0 && loadAttempted && !expertsInProgress && !!expertsError;

  const title = 'Meet the Masters';
  const description =
    'Browse the Uptales roster — vetted storytelling Masters across business, written, visual, and motion story. Every one passed a founder conversation before joining.';

  return (
    <Page
      title={title}
      description={description}
      scrollingDisabled={scrollingDisabled}
      schema={{ '@context': 'http://schema.org', '@type': 'WebPage', name: title, description }}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer currentPage="ExpertsPage" />}
        footer={<FooterContainer />}
      >
        <div className={css.root}>
          {/* ============ Index head ============ */}
          <header className={css.hero}>
            <div className={css.wrap}>
              <p className={css.eyebrow}>The Uptales roster</p>
              <h1 className={css.heroTitle}>
                Book the one{' '}
                <span className={css.penWrap}>
                  who’s done it
                  <PenUnderline />
                </span>
                .
              </h1>
              <p className={css.heroSub}>
                No influencers, no theorists — every Master here <b>built the thing they teach</b>.
                Pick a discipline below, or search a name you already trust.
              </p>

              {!isLoading && !isFailed ? (
                <div className={css.heroTools}>
                  {!isSearching && groups.length > 1 ? (
                    <nav className={css.chips} aria-label="Disciplines">
                      {groups.map(group => (
                        <a key={group.id} className={css.chip} href={`#${group.id}`}>
                          {group.title} <b>{group.experts.length}</b>
                        </a>
                      ))}
                    </nav>
                  ) : null}
                  <div className={css.searchRow}>
                    <label htmlFor="master-search" className={css.srOnly}>
                      Search Masters by name
                    </label>
                    <input
                      id="master-search"
                      className={css.searchInput}
                      type="search"
                      placeholder="Search by name…"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      autoComplete="off"
                    />
                    {isSearching ? (
                      <button
                        type="button"
                        className={css.searchClear}
                        onClick={() => setSearchQuery('')}
                      >
                        Clear
                      </button>
                    ) : (
                      <CaveatPointer />
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </header>

          {/* ============ Roster ============ */}
          <main className={css.roster}>
            <div className={css.wrap}>
              {isLoading ? (
                <SkeletonRoster />
              ) : isFailed ? (
                <div className={css.stateCard}>
                  <h2 className={css.stateTitle}>The roster didn’t load.</h2>
                  <p className={css.stateCopy}>
                    Something interrupted the request. It’s usually temporary.
                  </p>
                  <button type="button" className={css.retryButton} onClick={() => onLoadExperts()}>
                    Try again
                  </button>
                </div>
              ) : isSearching ? (
                searchResults.length > 0 ? (
                  <DisciplineSection
                    id="search-results"
                    title="Results"
                    blurb={`${searchResults.length} ${
                      searchResults.length === 1 ? 'Master matches' : 'Masters match'
                    } “${searchQuery.trim()}”.`}
                    experts={searchResults}
                    alwaysExpanded
                  />
                ) : (
                  <div className={css.stateCard}>
                    <h2 className={css.stateTitle}>No Master by that name yet.</h2>
                    <p className={css.stateCopy}>
                      Try a different spelling, or browse the roster by discipline.
                    </p>
                    <button
                      type="button"
                      className={css.retryButton}
                      onClick={() => setSearchQuery('')}
                    >
                      Clear search
                    </button>
                  </div>
                )
              ) : (
                groups.map(group => (
                  <DisciplineSection
                    key={group.id}
                    id={group.id}
                    title={group.title}
                    blurb={group.blurb}
                    experts={group.experts}
                  />
                ))
              )}
            </div>
          </main>

          {/* ============ The bar ============ */}
          <section className={css.bar}>
            <div className={classNames(css.wrap, css.barInner)}>
              <p className={css.barEyebrow}>The Uptales bar</p>
              <h2 className={css.barTitle}>
                No algorithm decides who’s <em>here</em>.
              </h2>
              <p className={css.barCopy}>
                Every Master on this page passed a working conversation with our founders before
                joining. Look for the <b>Top Advisor</b> mark — the strongest track records with
                builders.
              </p>
            </div>
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

ExpertsPageComponent.propTypes = {
  experts: array,
  expertsInProgress: bool,
  expertsError: propTypes.error,
  pageAssetsData: object,
  scrollingDisabled: bool.isRequired,
  onLoadExperts: func.isRequired,
};

const mapStateToProps = state => {
  const { experts = [], expertsInProgress = false, expertsError = null } = state.ExpertsPage || {};
  // The discipline sections are defined in the `our-experts` hosted asset,
  // which lands in the shared hostedAssets slice.
  const { pageAssetsData } = state.hostedAssets || {};
  return {
    experts,
    expertsInProgress,
    expertsError,
    pageAssetsData,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onLoadExperts: () => dispatch(loadExperts()).catch(() => null),
});

const ExpertsPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ExpertsPageComponent);

export default ExpertsPage;
